import type { FilterConditionPluginSpecification } from '../filter.plugin';
import { FilterConditionPluginImpl } from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';

/**
 * Wire-format condition id for the Fachgruppencode filter.
 *
 * Replaces the dropped `specialtyId` condition: the backend only accepts
 * `fachgruppencode` now and serialises to
 * `{ "fachgruppencode": { "includes": [...], "excludes": null } }`.
 *
 * The `includes` set holds raw KBV Anlage-2 Fachgruppencodes (e.g. `"01"`,
 * `"26"`, the `"00"` Unbekannt bucket, or the codes a synthetic `WEITERE`
 * catalog entry expands to). The catalog itself is fetched at runtime from
 * `GET /virtual-clinic-referral/fachgruppencode`, so the value type is a bare
 * `string[]` rather than a fixed domain union.
 */
export const BD_FACHGRUPPENCODE_CONDITION_ID = 'fachgruppencode';

export const FilterConditionFachgruppencodeType: FilterConditionPluginSpecification<
  string[],
  FilterConditionFachgruppencodeDto,
  FilterConditionFachgruppencodeCondition
> = {
  type: 'fachgruppencode',
  category: 'person',
  displayName: { de: 'Fachgruppe' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionFachgruppencodeDto,
  ): FilterConditionFachgruppencodePlugin {
    return new FilterConditionFachgruppencodePlugin(obj.includes ?? []);
  },
  fromCondition(
    jsonObj: FilterConditionFachgruppencodeCondition,
  ): FilterConditionFachgruppencodePlugin {
    return new FilterConditionFachgruppencodePlugin(jsonObj.includes ?? []);
  },
  newInstance(): FilterConditionFachgruppencodePlugin {
    return new FilterConditionFachgruppencodePlugin([]);
  },
};

export class FilterConditionFachgruppencodePlugin extends FilterConditionPluginImpl<
  string[],
  FilterConditionFachgruppencodeDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    string[],
    FilterConditionFachgruppencodeDto,
    FilterConditionFachgruppencodeCondition
  > = FilterConditionFachgruppencodeType;

  constructor(private includes: string[]) {
    super(FilterConditionFachgruppencodeType.type);
  }

  override clone(): FilterConditionFachgruppencodePlugin {
    return new FilterConditionFachgruppencodePlugin([...this.includes]);
  }

  override equals(other: FilterConditionFachgruppencodePlugin): boolean {
    return (
      super.equals(other) && BdArrayUtil.equals(this.includes, other.includes)
    );
  }

  override validationToText(): string {
    if (this.includes.length === 0) {
      return 'Mindestens eine Fachgruppe auswählen';
    } else {
      return '';
    }
  }

  override toCondition(): BdCondition {
    return new FilterConditionFachgruppencodeCondition(this.includes, []);
  }

  override toJSON(): FilterConditionFachgruppencodeDto {
    return {
      type: this.type,
      includes: this.includes,
    };
  }

  override toText(): string {
    return this.includes.length === 1
      ? 'eine Fachgruppe'
      : `${this.includes.length} Fachgruppen`;
  }

  override toValue(): string[] {
    return this.includes;
  }
}

class FilterConditionFachgruppencodeCondition extends BdContentCondition<string> {
  constructor(includes: string[], excludes: string[]) {
    super(BD_FACHGRUPPENCODE_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionFachgruppencodeDto {
  type: string;
  includes: string[];
}
